import { useState } from "react";
import { formatDateTime } from "../../api/dates";
import { useSubmitInspection, type InspectionDetail } from "../../api/inspections";

/** Submit: a checklist of what is in place and what still stands in the way,
 * and, when the inspection names a site that can still move, what the crew
 * found there. The submission is the assessment being completed; the outcome
 * is what happens next. What blocks a submission is the server's to say (its
 * `problems`); the valves' check counts are shown beside them so a crew can
 * jump straight to what is unanswered. */
export default function SubmitTab({ detail, onAnswer }: { detail: InspectionDetail; onAnswer: (valveId: number) => void }) {
  const submit = useSubmitInspection(detail.inspection.id);
  const [outcome, setOutcome] = useState("");
  const head = detail.inspection;
  if (!head.is_draft) {
    return (
      <div className="insp-section">
        <h3>Submitted</h3>
        <p className="notice ok">
          Submitted {formatDateTime(head.submitted_at)}. A quotation can now be started against this inspection on Commercial; field operators can no longer change it.
        </p>
      </div>
    );
  }
  const blocked = detail.problems.length > 0;
  return (
    <div className="insp-section">
      <h3>Submit the inspection</h3>
      <ul className="submit-list" aria-label="Before this can be submitted">
        {detail.valves.map((valve) => {
          const open = valve.total_checks - valve.answered;
          return (
            <li key={valve.id} className={open === 0 ? "ok" : "todo"}>
              <span className="submit-mark" aria-hidden="true">
                {open === 0 ? "✓" : "○"}
              </span>
              <span className="submit-text">
                {valve.name} · {open === 0 ? "all checks answered" : `${open} of ${valve.total_checks} checks unanswered`}
              </span>
              {open > 0 ? (
                <button type="button" className="go-link" onClick={() => onAnswer(valve.id)}>
                  Answer
                </button>
              ) : null}
            </li>
          );
        })}
        {detail.problems.map((p) => (
          <li key={p} className="blocked">
            <span className="submit-mark" aria-hidden="true">
              !
            </span>
            <span className="submit-text">{p}</span>
          </li>
        ))}
        {!blocked ? (
          <li className="ok">
            <span className="submit-mark" aria-hidden="true">
              ✓
            </span>
            <span className="submit-text">Everything required is in place.</span>
          </li>
        ) : null}
      </ul>
      {detail.actions.site_outcome_open ? (
        <section className="insp-outcomes">
          <h4>What did you find at the site?</h4>
          <div className="pill-row" role="radiogroup" aria-label="Outcome">
            {detail.options.outcomes.map((o) => (
              <label key={o.value} className={outcome === o.value ? "chip chip-on" : "chip"}>
                <input type="radio" name="outcome" value={o.value} checked={outcome === o.value} onChange={() => setOutcome(o.value)} /> {o.label}
              </label>
            ))}
            <label className={outcome === "" ? "chip chip-on" : "chip"}>
              <input type="radio" name="outcome" value="" checked={outcome === ""} onChange={() => setOutcome("")} /> Leave the site as it is
            </label>
          </div>
        </section>
      ) : null}
      <div className="submit-go">
        <button
          type="button"
          className="btn btn-primary btn-big"
          disabled={submit.isPending || blocked}
          onClick={() => submit.mutate(outcome || null)}
        >
          {submit.isPending ? "Submitting…" : "Submit inspection"}
        </button>
        <p className="insp-section-sub">Once submitted it can be quoted from on Commercial. Field operators can't change it after that.</p>
      </div>
      {submit.isError ? <p className="notice bad">The inspection was not submitted: {submit.error.message}</p> : null}
    </div>
  );
}
